/**
 * **Pandangan MEJA** — isi kolom kiri layar kasir, bukan layar tersendiri (Imron, 2026-08-02).
 *
 * ⭐ Ia menempati container yang SAMA dengan menu; yang berganti hanya isinya. Konsekuensinya:
 * bill tetap terlihat saat pelayan berpindah meja, dan tak ada chrome yang digandakan.
 *
 * Alur yang dilayani (VTEC mobile, dikonfirmasi Imron): buka meja → **isi jumlah tamu** → pilih
 * menu → *send/hold* → **kembali ke sini** → buka meja berikutnya.
 *
 * ### ⭐ Tiga keadaan, bukan dua
 * `empty` · `occupied` · **`merged`**. Yang ketiga yang membuat layar ini berguna: tanpa penanda
 * itu, pelayan mendudukkan tamu baru di meja yang mejanya sudah didorong menempel ke rombongan
 * sebelah — dan itu ketahuan setelah tamunya duduk.
 *
 * ⚠️ Jumlah tamu diminta **saat membuka meja**, bukan belakangan. Tapi karena pelayan sering salah
 * ketik di jam sibuk, angkanya juga bisa **dikoreksi** dari sini — tanpa membatalkan pesanan.
 * Tanpa jalur koreksi, satu-satunya cara memperbaikinya adalah void, dan void yang dipakai untuk
 * salah ketik membuat kontrol void aus.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';

export interface Table {
  readonly tableCode: string;
  readonly seats: number;
  readonly areaName: string | null;
  readonly orderId: string | null;
  readonly orderNo: string | null;
  readonly mergedUnder: string | null;
  readonly mergedChildren: readonly string[];
  /** ⭐ Siapa yang sedang memegang kunci sunting bill meja ini; `null`/tak ada = bebas. */
  readonly heldByName?: string | null;
}

export interface TableDashboardProps {
  readonly outletCode: string;
  /** Membuka pesanan baru di meja itu. */
  readonly onOpenTable: (tableCode: string, guestCount: number) => void;
  /** Masuk ke pesanan yang sudah ada. */
  readonly onEnterOrder: (orderId: string) => void;
}

export function TableDashboard(p: TableDashboardProps) {
  const { t } = useTranslation();
  const [table, setTable] = useState<readonly Table[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Table | null>(null);
  const [guest, setGuests] = useState('2');
  const [mergeParent, setMergeParent] = useState<string | null>(null);

  const load = useCallback(() => {
    void api
      .tables(p.outletCode)
      .then((r) => { setTable(r); setError(null); })
      /*
       * ⚠️ Gagal muat ⇒ daftar KOSONG dengan pesan, bukan daftar lama yang basi. Meja yang
       * ditampilkan kosong padahal terisi jauh lebih berbahaya daripada layar yang mengaku gagal.
       */
      .catch(() => { setTable([]); setError(t('table.loadFailed')); });
  }, [p.outletCode, t]);

  useEffect(() => { load(); }, [load]);

  const state = (m: Table): 'empty' | 'occupied' | 'merged' =>
    m.mergedUnder !== null ? 'merged' : m.orderId !== null ? 'occupied' : 'empty';

  /*
   * ⭐ **Dikelompokkan menurut AREA, bukan satu grid panjang.**
   *
   * Pelayan tak mencari "meja 14"; ia mencari *"meja kosong di Teras"* — karena ia sedang berdiri
   * di Teras. Grid tunggal memaksa ia memindai seluruh outlet untuk menjawab pertanyaan yang
   * cakupannya hanya satu ruangan, dan jam sibuk adalah waktu terburuk untuk memindai.
   *
   * ⚠️ Urutan area mengikuti urutan yang datang dari server (`area.sort_order`) — bukan diurutkan
   * ulang menurut abjad di sini. Urutan area adalah **denah**, dan denah itu milik outlet.
   */
  const area: { name: string | null; table: Table[] }[] = [];
  for (const m of table) {
    const ada = area.find((a) => a.name === m.areaName);
    if (ada === undefined) area.push({ name: m.areaName, table: [m] });
    else ada.table.push(m);
  }

  const tile = (m: Table) => {
    const k = state(m);
    const parent = m.mergedChildren.length > 0;
    return (
            <button
              key={m.tableCode}
              type="button"
              className={`table-tile ${k}${parent ? ' parent' : ''}`}
              data-testid={`table-${m.tableCode}`}
              data-state={k}
              onClick={() => {
                if (mergeParent !== null && mergeParent !== m.tableCode) {
                  void api
                    .mergeTables(p.outletCode, {
                      parentTableCode: mergeParent, childTableCodes: [m.tableCode],
                    })
                    .then(() => { setMergeParent(null); load(); })
                    .catch((e: Error) => { setError(e.message); });
                  return;
                }
                if (m.orderId !== null) { p.onEnterOrder(m.orderId); return; }
                // ⛔ Meja yang sedang digabung tak bisa dibuka sendiri — bill-nya milik induknya.
                if (m.mergedUnder !== null) return;
                setSelected(m);
                setGuests('2');
              }}
            >
              <span className="table-code">{m.tableCode}</span>
              <span className="table-seats">{t('table.seats', { n: m.seats })}</span>
              <span className="table-status">
                {k === 'merged'
                  ? t('table.merged', { parent: m.mergedUnder })
                  : k === 'occupied'
                    ? `${t('table.occupied')} · ${m.orderNo ?? ''}`
                    : t('table.empty')}
              </span>
              {/*
                ⭐⭐⭐ **Meja yang billnya sedang DIPEGANG orang lain — terlihat SEBELUM diketuk.**

                ⛔ Sebelum ini kunci sunting cuma terasa saat DITOLAK: pelayan mengetuk meja,
                menunggu jaringan, lalu dijawab "bill ini sedang dipegang …". Datanya sudah ada di
                `order.edit_held_by_*` sejak kunci itu lahir — cuma tak pernah ikut ke layar lantai.
                (Dilaporkan Imron 2026-08-06; log hari itu memuat 5× `ORDER-409-IN_USE`.)

                ⚠️ Mejanya sengaja TIDAK dimatikan: memegang kunci bukan berarti memilikinya
                selamanya, dan paksa-buka memang ada justru untuk HP pelayan yang mati di tengah
                jalan. Yang berubah cuma: kasir TAHU sebelum mengetuk, bukan sesudah ditolak.
              */}
              {(m.heldByName ?? null) !== null && (
                <span className="table-held" data-testid={`held-${m.tableCode}`}>
                  {t('table.heldBy', { name: m.heldByName })}
                </span>
              )}
              {parent && (
                <span className="table-children" data-testid={`children-${m.tableCode}`}>
                  + {m.mergedChildren.join(', ')}
                </span>
              )}
            </button>
    );
  };

  return (
    <section className="table" aria-label={t('table.title')} data-testid="table-dashboard">
      <div className="table-head">
        {/*
          ⚠️ `<h2>`, bukan `<h1>`: sejak meja jadi ISI layar kasir (bukan layar tersendiri), satu
          layar hanya boleh punya SATU judul tingkat satu. Dua `<h1>` membuat pembaca layar
          mengumumkan dua halaman di tempat yang sebenarnya satu.
        */}
        <h2>{t('table.title')}</h2>
        {/* Aksi mode duduk berdampingan dengan judulnya — bukan terdampar di kaki daftar. */}
        <button
          type="button"
          className={mergeParent === null ? '' : 'action-secondary'}
          data-testid="start-merge"
          onClick={() => { setMergeParent(mergeParent === null ? (table[0]?.tableCode ?? null) : null); }}
        >
          {t('table.merge')}
        </button>
      </div>

      {error !== null && <p className="error" data-testid="table-error">{error}</p>}

      {mergeParent !== null && (
        <p className="dialog-note" data-testid="merge-hint">
          {t('table.chooseParent')} — {mergeParent}
        </p>
      )}

      {area.map((a) => (
        <div className="table-area" key={a.name ?? '—'}>
          {/* Nama area muncul sekali sebagai kepala, bukan diulang di tiap ubin. */}
          {a.name !== null && <h2 className="table-area-name">{a.name}</h2>}
          <div className="table-grid">{a.table.map(tile)}</div>
        </div>
      ))}

      {/* ── buka meja: jumlah tamu diminta DI SINI ────────────────────────── */}
      {selected !== null && (
        <div className="dialog" role="dialog" aria-label={t('table.openTable')} data-testid="table-dialog">
          <h2>{selected.tableCode}</h2>
          <label>
            {t('table.guest')}
            <input
              type="number"
              inputMode="numeric"
              data-testid="table-guests"
              value={guest}
              onChange={(e) => setGuests(e.target.value)}
            />
          </label>
          <div className="dialog-actions">
            <button type="button" data-testid="table-cancel" onClick={() => { setSelected(null); }}>
              {t('table.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="table-open"
              disabled={guest === '' || Number(guest) < 1}
              onClick={() => {
                p.onOpenTable(selected.tableCode, Number(guest));
                setSelected(null);
              }}
            >
              {t('table.enterOrder')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
